import { SplatUserType } from '@sunrun/sales-experience-shared';

import { DBChatroomData, UserRole, UserRoleType } from './db-interfaces';

type GetUnreadCountParams = {
  chatroomData: DBChatroomData | null | undefined;
  contactId: string | null | undefined;
  role: UserRoleType | null | undefined;
};

const collectSunrunParticipantIds = (
  ...ids: Array<string | null | undefined>
): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();

  for (const raw of ids) {
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    out.push(trimmed);
  }

  return out;
};

const getChatroomCustomerContactIds = (
  firestoreDocumentId: string,
  chatroomData: DBChatroomData,
): string[] =>
  collectSunrunParticipantIds(firestoreDocumentId, chatroomData.id);

const getChatUserRoleFromSplatUserType = (
  splatUserType: SplatUserType | undefined,
): UserRoleType | undefined => {
  if (splatUserType === 'CX Pro') {
    return UserRole.CX_PRO;
  }
  if (splatUserType) {
    return UserRole.SALES_REP;
  }
  return undefined;
};

/**
 * Matches sales-chat `getUnreadCountForContact`:
 * prefers `unreadCountsByContactId[contactId]`, then role-based legacy fields.
 */
const getUnreadCountForCurrentUser = ({
  chatroomData,
  contactId,
  role,
}: GetUnreadCountParams): number => {
  if (!chatroomData || !contactId) {
    return 0;
  }

  if (chatroomData.unreadCountsByContactId) {
    if (
      contactId in chatroomData.unreadCountsByContactId &&
      chatroomData.unreadCountsByContactId[contactId] >= 0
    ) {
      return chatroomData.unreadCountsByContactId[contactId] ?? 0;
    }
  }

  if (role === UserRole.SALES_REP) {
    const count = chatroomData.numUnreadMessagesSalesRep ?? 0;
    return count > 0 ? count : 0;
  }
  if (role === UserRole.CX_PRO) {
    const count = chatroomData.numUnreadMessagesCxPro ?? 0;
    return count > 0 ? count : 0;
  }
  if (role === UserRole.CUSTOMER) {
    const count = chatroomData.numUnreadMessagesCustomer ?? 0;
    return count > 0 ? count : 0;
  }

  return 0;
};

// try each known Sunrun contact id (JWT claim vs profile)
const getUnreadCountForParticipant = ({
  chatroomData,
  contactIds,
  role,
}: {
  chatroomData: DBChatroomData;
  contactIds: string[];
  role: UserRoleType;
}): number => {
  for (const contactId of contactIds) {
    const count = getUnreadCountForCurrentUser({
      chatroomData,
      contactId,
      role,
    });
    if (count > 0) {
      return count;
    }
  }

  return 0;
};

export {
  collectSunrunParticipantIds,
  getChatroomCustomerContactIds,
  getChatUserRoleFromSplatUserType,
  getUnreadCountForCurrentUser,
  getUnreadCountForParticipant,
};
